import type { ReactNode } from 'react'
import { ProvenanceChip } from '@/components/data-truth/ProvenanceChip'
import type { Provenance } from '@/components/data-truth/provenance'
import { StatusChip } from '@/components/ui/status-chip'
import { cn } from '@/lib/utils'

export type WidgetSpan = 3 | 4 | 6 | 8 | 12

const SPAN_CLASS: Record<WidgetSpan, string> = {
  3: 'col-span-3',
  4: 'col-span-4',
  6: 'col-span-6',
  8: 'col-span-8',
  12: 'col-span-12',
}

/**
 * The widget shell every dashboard composes from (docs/design/00-design-system.md
 * §4): title + provenance + body + footer drill-down links. Bodies own their own
 * padding when they need edge-to-edge tables.
 */
export function Widget({
  title,
  badges,
  provenance,
  sampleData,
  footer,
  span = 6,
  bodyPadding = true,
  children,
  className,
}: {
  title: ReactNode
  badges?: ReactNode
  provenance?: Provenance
  /** No backing /admin/v2 endpoint exists yet — badge this widget so sample data is never mistaken for live (data-truth rule). */
  sampleData?: boolean
  footer?: ReactNode
  span?: WidgetSpan
  bodyPadding?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn(SPAN_CLASS[span], 'overflow-hidden rounded-[10px] border border-border bg-surface', className)}>
      <div className="flex items-center justify-between border-b border-border px-3.5 py-2.5">
        <div className="flex items-center gap-1.5 text-[12.5px] font-bold">
          {title}
          {badges}
          {sampleData && (
            <StatusChip status="unknown" stale>
              sample data
            </StatusChip>
          )}
        </div>
        {provenance && <ProvenanceChip provenance={provenance} />}
      </div>
      <div className={bodyPadding ? 'p-3.5' : ''}>{children}</div>
      {footer && <div className="border-t border-border px-3.5 py-2 text-[11.5px]">{footer}</div>}
    </div>
  )
}
